import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AdminDashboardView from "../components/dashboard/AdminDashboardView";
import CadreDashboardView from "../components/dashboard/CadreDashboardView";
import ClientDashboardView from "../components/dashboard/ClientDashboardView";
import CommunityDashboardView from "../components/dashboard/CommunityDashboardView";
import PanelAdminDashboardView from "../components/dashboard/PanelAdminDashboardView";
// Modal layout handled by reusable CadreDetailsModal
import CadreDetailsModal from "../components/shared/CadreDetailsModal";
import PROJECT_CONFIG from "../config/projectConfig";
import { useAuth } from "../hooks/useAuth";
import { cadreAPI, dashboardAPI, panelAPI, userAPI } from "../services/api";
import {
  exportAdminReport,
  exportToExcel,
  formatApplicationsForExport,
  formatCadreMembersForExport,
  formatPanelsForExport,
} from "../utils/excelExport";

const Dashboard = () => {
  const { user, hasRole, isPanelAdmin } = useAuth();
  const navigate = useNavigate();

  // Determine active dashboard type based on user role
  const [activeDashboard, setActiveDashboard] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state for cadre members
  const [cadrePagination, setCadrePagination] = useState({
    current_page: 1,
    total_pages: 1,
    has_next_page: false,
    has_previous_page: false,
    next_page: null,
    previous_page: null,
  });

  // Loading states
  const [cadreLoading, setCadreLoading] = useState(false);

  // Modal state for member profile
  const [showMemberModal, setShowMemberModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    // Determine which dashboard to show
    if (hasRole("PLATFORM_ADMIN")) {
      setActiveDashboard("admin");
    } else if (hasRole("CADRE_MEMBER")) {
      // Check if cadre member is also a panel admin
      if (isPanelAdmin()) {
        setActiveDashboard("panelAdmin");
      } else {
        setActiveDashboard("cadre");
      }
    } else if (hasRole("COMMUNITY_MEMBER")) {
      setActiveDashboard("community");
    } else if (hasRole("CLIENT_MEMBER")) {
      setActiveDashboard("client");
    } else {
      setActiveDashboard("default");
    }
  }, [hasRole, isPanelAdmin]);

  const handleNavigate = (path) => {
    const routeMap = {
      "panel-creation": "/panel-creation",
      panels: "/panels",
      "panels-management": "/panels-management",
      "cadre-application": "/cadre-application",
      "cadre-profile": "/cadre-profile",
      "shortlist-review": "/shortlist-review",
      "srp-rules": "/srp-rules",
      resources: "/resources",
      faqs: "/faqs",
      contact: "/contact",
      "request-panel": "/request-panel",
      "upload-documents": "/upload-documents",
    };

    if (path.startsWith("panel-details/")) {
      navigate(`/panel/${path.split("/")[1]}`);
    } else if (path.startsWith("panel-admin/")) {
      navigate(`/panel-admin/${path.split("/")[1]}`);
    } else if (routeMap[path]) {
      navigate(routeMap[path]);
    } else {
      navigate(`/${path}`);
    }
  };

  // Centralized API call handler based on dashboard type
  const fetchDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      switch (activeDashboard) {
        case "admin":
          await fetchAdminData(1); // Always start from page 1 on refresh
          break;
        case "panelAdmin":
          await fetchPanelAdminData();
          break;
        case "cadre":
          await fetchCadreData();
          break;
        case "community":
          await fetchCommunityData();
          break;
        case "client":
          await fetchClientData();
          break;
        default:
          break;
      }
    } catch (err) {
      console.error("Error fetching dashboard data:", err);
      setError("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, [activeDashboard]);

  useEffect(() => {
    if (activeDashboard && activeDashboard !== "default") {
      fetchDashboardData();
    }
  }, [activeDashboard, fetchDashboardData]);

  const fetchAdminData = async (page = 1) => {
    const [
      statsResponse,
      panelsResponse,
      appsResponse,
      usersResponse,
      cadreResponse,
    ] = await Promise.all([
      dashboardAPI
        .getAdminDashboard()
        .catch((err) => ({ success: false, error: err })),
      panelAPI.getAllPanels().catch((err) => ({ success: false, error: err })),
      // High limit so older pending applicants are not truncated off page 1.
      cadreAPI
        .getAllApplications({ page: 1, limit: 1000 })
        .catch((err) => ({ success: false, error: err })),
      userAPI.getAllUsers().catch((err) => ({ success: false, error: err })),
      cadreAPI
        .getCadreMembers({ page, limit: 10 })
        .catch((err) => ({ success: false, error: err })),
    ]);

    setDashboardData({
      stats: statsResponse.success ? statsResponse.stats : {},
      panels: panelsResponse.success ? panelsResponse.panels || [] : [],
      applications: appsResponse.success ? appsResponse.applications || [] : [],
      adminUsers: usersResponse.success
        ? usersResponse.users.filter((u) => u.role === "PLATFORM_ADMIN")
        : [],
      cadreMembers: cadreResponse.success ? cadreResponse.members || [] : [],
    });

    // Update pagination state if cadre response is successful
    if (cadreResponse.success && cadreResponse.pagination) {
      setCadrePagination(cadreResponse.pagination);
    }
  };

  const fetchPanelAdminData = async () => {
    const [statsResponse, panelsResponse] = await Promise.all([
      dashboardAPI
        .getPanelAdminDashboard()
        .catch((err) => ({ success: false, error: err })),
      panelAPI.getAllPanels().catch((err) => ({ success: false, error: err })),
    ]);

    setDashboardData({
      statistics: statsResponse.success ? statsResponse.stats : {},
      panels: panelsResponse.success ? panelsResponse.panels || [] : [],
    });
  };

  const fetchCadreData = async () => {
    const [statsResponse, panelsResponse] = await Promise.all([
      dashboardAPI
        .getCadreDashboard()
        .catch((err) => ({ success: false, error: err })),
      panelAPI
        .getMemberPanels()
        .catch((err) => ({ success: false, error: err })),
    ]);

    setDashboardData({
      statistics: statsResponse.success ? statsResponse.stats : {},
      panels: panelsResponse.success ? panelsResponse.panels || [] : [],
    });
  };

  const fetchCommunityData = async () => {
    const [statsResponse, panelsResponse, publicResponse] = await Promise.all([
      dashboardAPI
        .getCommunityDashboard()
        .catch((err) => ({ success: false, error: err })),
      panelAPI
        .getCommunityPanels()
        .catch((err) => ({ success: false, error: err })),
      panelAPI
        .getPublicPanels()
        .catch((err) => ({ success: false, error: err })),
    ]);

    setDashboardData({
      statistics: statsResponse.success ? statsResponse.stats : {},
      panels: panelsResponse.success ? panelsResponse.panels || [] : [],
      publicPanels: publicResponse.success ? publicResponse.panels || [] : [],
    });
  };

  const fetchClientData = async () => {
    const [statsResponse, panelsResponse] = await Promise.all([
      dashboardAPI
        .getClientDashboard()
        .catch((err) => ({ success: false, error: err })),
      panelAPI
        .getClientPanels()
        .catch((err) => ({ success: false, error: err })),
    ]);

    setDashboardData({
      statistics: statsResponse.success ? statsResponse.stats : {},
      panels: panelsResponse.success ? panelsResponse.panels || [] : [],
    });
  };

  // Optimized function to fetch only cadre members
  const fetchCadreMembersOnly = async (page = 1) => {
    try {
      setCadreLoading(true);
      const cadreResponse = await cadreAPI.getCadreMembers({ page, limit: 10 });

      if (cadreResponse.success) {
        setDashboardData((prev) => ({
          ...prev,
          cadreMembers: cadreResponse.members || [],
        }));

        if (cadreResponse.pagination) {
          setCadrePagination(cadreResponse.pagination);
        }
      }
    } catch (err) {
      console.error("Error fetching cadre members:", err);
      toast.error("Failed to load cadre members");
    } finally {
      setCadreLoading(false);
    }
  };

  // Handle cadre members pagination
  const handleCadrePageChange = async (page) => {
    if (page && page !== cadrePagination.current_page) {
      await fetchCadreMembersOnly(page);
    }
  };

  // Action handlers for admin dashboard
  const handleAdminAction = async (action) => {
    try {
      switch (action.type) {
        case "approveApplication":
          await cadreAPI.approveApplication(action.id);
          toast.success("Application approved successfully!");
          await fetchDashboardData();
          break;

        case "rejectApplication":
          const reason =
            action.reason || prompt("Please provide a reason for rejection:");
          if (reason) {
            await cadreAPI.rejectApplication(action.id, reason);
            toast.warning("Application rejected.");
            await fetchDashboardData();
          }
          break;

        case "deleteApplication":
          await cadreAPI.deleteApplication(action.id);
          toast.success("Application deleted.");
          await fetchDashboardData();
          break;

        case "approvePanel":
          await panelAPI.updatePanelStatus(action.id, "Active");
          toast.success("Panel approved successfully!");
          await fetchDashboardData();
          break;

        case "publishPanel":
          await panelAPI.publishPanel(action.id);
          toast.success("Panel published successfully!");
          await fetchDashboardData();
          break;

        case "closePanel":
          await panelAPI.updatePanelStatus(action.id, "Closed");
          toast.success("Panel closed successfully!");
          await fetchDashboardData();
          break;

        case "viewMemberDetails":
          setSelectedMember(action.member);
          setShowMemberModal(true);
          break;

        case "exportFullReport":
          const result = exportAdminReport({
            panels: dashboardData.panels,
            applications: dashboardData.applications,
            cadreMembers: dashboardData.cadreMembers,
          });
          if (result.success) {
            toast.success("Full report exported successfully!");
          } else {
            toast.error("Failed to export full report");
          }
          break;

        case "exportPanels":
          const panelsData = formatPanelsForExport(dashboardData.panels);
          const panelsResult = exportToExcel(
            panelsData,
            "APEX_Panels",
            "Panels"
          );
          if (panelsResult.success) {
            toast.success("Panels exported successfully!");
          } else {
            toast.error("Failed to export panels");
          }
          break;

        case "exportApplications":
          const appsData = formatApplicationsForExport(
            dashboardData.applications
          );
          const appsResult = exportToExcel(
            appsData,
            "APEX_Applications",
            "Applications"
          );
          if (appsResult.success) {
            toast.success("Applications exported successfully!");
          } else {
            toast.error("Failed to export applications");
          }
          break;

        case "exportCadreMembers":
          const membersData = formatCadreMembersForExport(
            dashboardData.cadreMembers
          );
          const membersResult = exportToExcel(
            membersData,
            "APEX_Cadre_Members",
            "Cadre Members"
          );
          if (membersResult.success) {
            toast.success("Cadre members exported successfully!");
          } else {
            toast.error("Failed to export cadre members");
          }
          break;

        default:
          console.log("Unknown action:", action.type);
      }
    } catch (err) {
      console.error("Error handling admin action:", err);
      toast.error("Failed to perform action");
    }
  };

  const renderDashboard = () => {
    if (activeDashboard === "default") {
      return (
        <div className="p-6">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900">
              Welcome to {PROJECT_CONFIG.name} Platform
            </h2>
            <p className="text-gray-600 mt-2">
              Please contact support if you're having trouble accessing your
              dashboard.
            </p>
          </div>
        </div>
      );
    }

    if (loading) {
      return (
        <div className="p-6 flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-blue mx-auto mb-4"></div>
            <p className="text-gray-600">Loading dashboard...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="p-6">
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            <p>{error}</p>
            <button
              onClick={fetchDashboardData}
              className="mt-2 text-red-900 underline text-sm"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    // Render appropriate dashboard based on active type
    switch (activeDashboard) {
      case "admin":
        return (
          <AdminDashboardView
            user={user}
            data={dashboardData}
            onNavigate={handleNavigate}
            onRefresh={fetchDashboardData}
            onAction={handleAdminAction}
            cadrePagination={cadrePagination}
            onCadrePageChange={handleCadrePageChange}
            cadreLoading={cadreLoading}
          />
        );
      case "panelAdmin":
        return (
          <PanelAdminDashboardView
            user={user}
            data={dashboardData}
            onNavigate={handleNavigate}
            onRefresh={fetchDashboardData}
          />
        );
      case "cadre":
        return (
          <CadreDashboardView
            user={user}
            data={dashboardData}
            onNavigate={handleNavigate}
            onRefresh={fetchDashboardData}
          />
        );
      case "community":
        return (
          <CommunityDashboardView
            user={user}
            data={dashboardData}
            onNavigate={handleNavigate}
            onRefresh={fetchDashboardData}
          />
        );
      case "client":
        return (
          <ClientDashboardView
            user={user}
            data={dashboardData}
            onNavigate={handleNavigate}
            onRefresh={fetchDashboardData}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-[calc(100vh-200px)] bg-gray-50">
      <div className="max-w-7xl mx-auto">{renderDashboard()}</div>

      {/* Member Profile Modal (reusable design) */}
      <CadreDetailsModal
        isOpen={showMemberModal}
        onClose={() => {
          setShowMemberModal(false);
          setSelectedMember(null);
        }}
        cadre={selectedMember}
        showSelectButton={false}
      />
    </div>
  );
};

export default Dashboard;
